import {
	PropsWithChildren,
	ReactElement,
	KeyboardEvent
} from "react";
import classNames from "classnames";
import styles from "./CalendarCell.module.less";
import { ButtonBase } from "../ButtonBase/ButtonBase";
import { KeyEventArguments } from "../TextInput/TextInput";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { Key } from "client/Misc/commonTypes";
import { isNotNil, setTrackingId } from "client/Misc/utils";

type CalendarCellKind =
	| "normal"
	| "header"
	| "week-number"
	| "month"
	| "year";

const kindToStyles: { [key in CalendarCellKind]: string | undefined } = {
	["normal"]: undefined,
	["header"]: styles.content_header,
	["week-number"]: styles.content_weekNumber,
	["month"]: undefined,
	["year"]: undefined
};

export type CalendarCellProps = {
	kind: CalendarCellKind;
	onClick?(): void;
	isDisabled?: boolean;
	isOutOfRange?: boolean;
	isActive?: boolean;
	isNextActive?: boolean;
	isPreviousActive?: boolean;
	isToday?: boolean;
	hasEvent?: boolean;
	isHovered?: boolean;
	isNextHovered?: boolean;
	isPreviousHovered?: boolean;
	onMouseEnter?(): void;
	onMouseLeave?(): void;
	onKeyDown?: (event: KeyEventArguments) => boolean;
	tabIndex?: number;
	ref?: (element: HTMLElement | null) => void;
	ariaLabel?: string;
	trackingId: string;
};

export const CalendarCell = (props: PropsWithChildren<CalendarCellProps>): ReactElement => {
	const {
		kind,
		onClick,
		isDisabled = false,
		isOutOfRange = false,
		isActive = false,
		isNextActive = false,
		isToday = false,
		hasEvent = false,
		isHovered = false,
		isNextHovered = false,
		isPreviousActive = false,
		isPreviousHovered = false,
		onMouseEnter,
		onMouseLeave,
		children,
		onKeyDown,
		ref,
		tabIndex,
		ariaLabel,
		trackingId
	} = props;

	const isClickable: boolean = isNotNil(onClick) && !isDisabled;
	const RootComponent = isClickable ? ButtonBase : "div";

	const keydownHandler = (event: KeyboardEvent): void => {
		if (!onKeyDown) {
			return;
		}

		const key: Key | null = tryResolveKeyboardKey(event);

		const result: boolean = onKeyDown({
			key,
			modifiers: {
				shift: event.shiftKey,
				capsLock: event.getModifierState("CapsLock")
			}
		});

		if (result) {
			event.preventDefault();
		}
	};

	return (
		<RootComponent
			onKeyDown={keydownHandler}
			ref={ref}
			tabIndex={tabIndex}
			className={classNames(
				styles.root,
				{
					[styles.root_header]: kind === "header",
					[styles.root_weekNumber]: kind === "week-number",
				}
			)}
			onClick={isClickable ? onClick : undefined}
			onMouseEnter={isDisabled ? undefined : onMouseEnter}
			onMouseLeave={isDisabled ? undefined : onMouseLeave}
			role={kind === "header" ? "columnheader" : "gridcell"}
			aria-disabled={isDisabled}
			aria-selected={isActive}
			aria-hidden={kind === "week-number"}
			aria-label={ariaLabel}
		>
			<div
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.content,
					kindToStyles[kind],
					{
						[styles.content_clickable]: isClickable,
						[styles.content_disabled]: isDisabled,
						[styles.content_outOfRange]: isOutOfRange,
						[styles.content_active]: isActive,
						[styles.content_hover]: isHovered,
						[styles.content_nextActive]: isNextActive,
						[styles.content_nextHover]: isNextHovered,
						[styles.content_previousActive]: isPreviousActive,
						[styles.content_previousHover]: isPreviousHovered,
						[styles.content_isToday]: isToday
					}
				)}
			>
				{children}

				{hasEvent && (
					<div className={styles.dot} />
				)}
			</div>
		</RootComponent>
	);
};
CalendarCell.displayName = "CalendarCell";